export interface RelationHop {
  targetEntity: string;
  searchAttribute: string;
  extractAttribute: string;
}

export interface RelationHopOption {
  label: string;
  value: string;
  hop: RelationHop;
}

export interface RelationDropdownConfig {
  id: string;
  label: string;
  placeholder?: string;
  required?: boolean;
  mapTo?: keyof RelationHop;
  options?: RelationHopOption[] | Array<{ label: string; value: string }>;
  dependsOn?: string[];
  /** Optional replacement for inline options; all paths are dot-separated. */
  optionsApi?: {
    url: string;
    method?: 'GET' | 'POST';
    body?: unknown;
    responsePath?: string;
    labelPath: string;
    valuePath: string;
    /** Defaults to the row itself when omitted. */
    hopPath?: string;
    /** Maps backend field names onto the compute API's three hop keys. */
    hopFields?: Record<keyof RelationHop, string>;
  };
}

export interface RelationHopsConfig {
  enabled: boolean;
  categoryIds: string[];
  title?: string;
  hint?: string;
  dropdowns: RelationDropdownConfig[];
}

export function relationPath(value: unknown, path: string): unknown {
  return path.split('.').filter(Boolean).reduce<unknown>((current, key) => {
    if (['__proto__', 'prototype', 'constructor'].includes(key)) return undefined;
    return current && typeof current === 'object' ? (current as Record<string, unknown>)[key] : undefined;
  }, value);
}

/** Whole-value tokens preserve JSON types; URL tokens are encoded. */
export function relationTemplate(template: unknown, selections: Record<string, string>, encode = false): unknown {
  const context = { selections };
  if (typeof template === 'string') {
    const whole = /^\{(selections\.[\w.-]+)\}$/.exec(template);
    if (whole && !encode) return relationPath(context, whole[1]) ?? null;
    return template.replace(/\{(selections\.[\w.-]+)\}/g, (_, path: string) => {
      const value = String(relationPath(context, path) ?? '');
      return encode ? encodeURIComponent(value) : value;
    });
  }
  if (Array.isArray(template)) return template.map((item) => relationTemplate(item, selections));
  if (template && typeof template === 'object') {
    return Object.fromEntries(Object.entries(template).map(([key, value]) => [key, relationTemplate(value, selections)]));
  }
  return template;
}

export function isRelationHop(value: unknown): value is RelationHop {
  return !!value && typeof value === 'object' &&
    ['targetEntity', 'searchAttribute', 'extractAttribute'].every((key) => {
      const field = (value as Record<string, unknown>)[key];
      return typeof field === 'string' && !!field.trim();
    });
}

export function copyRelationHops(value: unknown): RelationHop[] {
  return Array.isArray(value) ? value.filter(isRelationHop).map(({ targetEntity, searchAttribute, extractAttribute }) =>
    ({ targetEntity, searchAttribute, extractAttribute })) : [];
}

export function sameRelationHop(first: RelationHop, second: RelationHop): boolean {
  return first.targetEntity === second.targetEntity && first.searchAttribute === second.searchAttribute && first.extractAttribute === second.extractAttribute;
}
